'use client';
import type { PublicUserDocument } from '@models/user';
import { getItem, logEvent } from '@util/analytics';
import {
  trackUserInteraction,
  trackUserSearch,
} from '@util/firestore/recommendations';
import { getProductSlug } from '@util/urlHelpers';
import { useWarehouse } from 'app/product/[slug]/components/WarehouseProvider';
import { isSelectedBikeComplete } from 'app/product/utils/vehicleSpecificDirect';
import { useAttributionContext } from 'context/AttributionContext';
import { useAuth } from 'context/AuthContext';
import { useBikeFilter } from 'context/BikeFilterContext';
import { useSearch } from 'context/SearchContext';
import type { OrderItemDocument } from 'models/order';
import { ProductLight } from 'models/product';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ListProductInfo } from './ListProductInfo';
import { ListProductPicture } from './ListProductPicture';

const MXListProductCard = ({
  product,
  attribution,
}: {
  product: ProductLight;
  seller?: PublicUserDocument;
  attribution?: OrderItemDocument['attribution'];
}) => {
  const pathname = usePathname() ?? '';
  const { userDoc } = useAuth();
  const { setAttribution } = useAttributionContext();
  const { setUseGroupTitle } = useWarehouse();
  const { selectedBike } = useBikeFilter();
  const { filters } = useSearch();
  return (
    <div className="group relative">
      <div className="flex items-start gap-2 self-stretch overflow-hidden rounded-brand bg-white sm:flex-col lg:flex-row">
        <ListProductPicture
          product={product}
          attribution={attribution}
          setAttribution={setAttribution}
        />
        <Link
          href={getProductSlug(product)}
          onClick={() => {
            logEvent('select_item', {
              items: [getItem(product)],
              item_list_id: pathname,
              item_list_name: pathname,
            });
            setAttribution(product.id, attribution);
            if (product.integration_info?.type === 'warehouse') {
              const isPart = product.category.toLowerCase().includes('part');
              if (!isPart || !isSelectedBikeComplete(selectedBike)) {
                setUseGroupTitle(true);
              }
            }
            if (pathname.includes('search')) {
              if (userDoc)
                trackUserInteraction({
                  uid: userDoc.uid,
                  pid: product.id,
                  interaction: 'search_clicked',
                  attribution,
                });
              if (filters?.term) {
                trackUserSearch({
                  ...filters,
                  product_clicked: product.id,
                });
              }
            }
          }}
          className="flex-1"
        >
          <ListProductInfo product={product} userDoc={userDoc} />
        </Link>
      </div>
    </div>
  );
};

export default MXListProductCard;
